/**
 * The chat rail's title bar: which conversation you are in, how to switch it,
 * and how to get the rail out of the way.
 *
 * What the switcher's entries *do* is the host's business — chat-home routes
 * to a conversation URL, while a product page hosting the rail beside its own
 * surface swaps the thread without leaving the page — so this takes handlers
 * rather than navigating itself.
 */

import {
  ChevronDown,
  Download,
  MessageSquareText,
  MoreHorizontal,
  Pencil,
  Plus,
  Share2,
  Trash2,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { conversationLabel } from '@/components/agent-chat';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { AgentConversationSummary } from '@/services/agents/config';

/**
 * The conversation a menu action was aimed at, captured when it was picked.
 * Handed to the host rather than looked up again on confirm: a browser
 * back/forward while a confirmation is up must not repoint the action at
 * whatever conversation is on screen by then.
 */
export interface ConversationMenuTarget {
  id: string;
  /** What it is called on screen — may be a preview, or '(untitled)'. */
  label: string;
  /** Its stored title, empty when it has never been named. */
  title: string;
}

export interface ChatRailHeaderProps {
  conversations: AgentConversationSummary[];
  conversationId: string | null;
  /** Title returned with a directly-opened conversation not in the list. */
  conversationTitle?: string | null;
  onNewConversation: () => void;
  onSelectConversation: (conversationId: string) => void;
  /**
   * Managing the conversation itself is optional: a product page hosting the
   * rail beside its own surface can leave these out, and the menu drops the
   * entries rather than offering something that does nothing.
   */
  onDownloadConversation?: () => void;
  onShareConversation?: () => void;
  onRenameConversation?: (target: ConversationMenuTarget) => void;
  onDeleteConversation?: (target: ConversationMenuTarget) => void;
  /** Omitted when there is no surface to fall back to. */
  onCollapse?: () => void;
}

export function ChatRailHeader({
  conversations,
  conversationId,
  conversationTitle,
  onNewConversation,
  onSelectConversation,
  onDownloadConversation,
  onShareConversation,
  onRenameConversation,
  onDeleteConversation,
  onCollapse,
}: ChatRailHeaderProps) {
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [query, setQuery] = useState('');

  const isSearching = query.trim() !== '';
  const matching = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return conversations;
    return conversations.filter((c) =>
      conversationLabel(c).toLowerCase().includes(needle),
    );
  }, [query, conversations]);

  const activeConversation = conversations.find(
    (c) => c.conversation_id === conversationId,
  );
  const title = conversationId
    ? activeConversation
      ? conversationLabel(activeConversation)
      : (conversationTitle ?? 'Chat')
    : 'New chat';

  const menuTarget: ConversationMenuTarget | null = conversationId
    ? {
        id: conversationId,
        label: title,
        title: activeConversation?.title ?? conversationTitle ?? '',
      }
    : null;

  const newChatItem = (
    <CommandGroup>
      <CommandItem
        value='__new_chat__'
        onSelect={() => {
          setSwitcherOpen(false);
          onNewConversation();
        }}
      >
        <Plus className='size-4' />
        New Chat
      </CommandItem>
    </CommandGroup>
  );

  return (
    <header className='flex h-12 shrink-0 items-center justify-between gap-2 px-2'>
      <Popover
        open={switcherOpen}
        onOpenChange={(open) => {
          setSwitcherOpen(open);
          if (!open) setQuery('');
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant='ghost'
            className='h-9 min-w-0 max-w-full justify-start px-4'
          >
            <MessageSquareText className='size-4 shrink-0' />
            <span className='truncate text-sm font-medium'>{title}</span>
            <ChevronDown className='size-4 shrink-0 text-muted-foreground' />
          </Button>
        </PopoverTrigger>
        <PopoverContent align='start' className='w-76 p-0'>
          <Command shouldFilter={false}>
            <CommandInput
              placeholder='Search Chats'
              value={query}
              onValueChange={setQuery}
            />
            <CommandList className='subtle-scrollbar'>
              {/* While searching, the matches come first so Enter opens one
                  instead of starting a new chat. */}
              {!isSearching && newChatItem}
              {matching.length > 0 && (
                <CommandGroup heading='Recent'>
                  {matching.map((c) => (
                    <CommandItem
                      key={c.conversation_id}
                      value={c.conversation_id}
                      onSelect={() => {
                        setSwitcherOpen(false);
                        onSelectConversation(c.conversation_id);
                      }}
                      className={cn(
                        c.conversation_id === conversationId && 'bg-accent/60',
                      )}
                    >
                      <MessageSquareText className='size-4 shrink-0' />
                      <span className='truncate'>{conversationLabel(c)}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {isSearching && matching.length === 0 && (
                <p className='py-6 text-center text-sm text-muted-foreground'>
                  No chats found.
                </p>
              )}
              {isSearching && newChatItem}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <div className='flex shrink-0 items-center gap-2'>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='size-9'
              aria-label='Chat options'
            >
              <MoreHorizontal className='size-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end' className='w-56'>
            <DropdownMenuItem onSelect={onNewConversation}>
              <Plus className='size-4' />
              New chat
            </DropdownMenuItem>
            {onRenameConversation && (
              <DropdownMenuItem
                disabled={!menuTarget}
                onSelect={() => {
                  if (menuTarget) onRenameConversation(menuTarget);
                }}
              >
                <Pencil className='size-4' />
                Rename chat
              </DropdownMenuItem>
            )}
            {onShareConversation && (
              <DropdownMenuItem
                disabled={!conversationId}
                onSelect={onShareConversation}
              >
                <Share2 className='size-4' />
                Share chat
              </DropdownMenuItem>
            )}
            {onDownloadConversation && (
              <DropdownMenuItem
                disabled={!conversationId}
                onSelect={onDownloadConversation}
              >
                <Download className='size-4' />
                Download chat
              </DropdownMenuItem>
            )}
            {onDeleteConversation && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  disabled={!menuTarget}
                  onSelect={() => {
                    if (menuTarget) onDeleteConversation(menuTarget);
                  }}
                >
                  <Trash2 className='size-4' />
                  Delete chat
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Only meaningful beside a surface — with nothing to fall back to,
            collapsing the rail would leave a blank page. */}
        {onCollapse && (
          <Button
            variant='ghost'
            size='icon'
            className='size-9'
            aria-label='Hide chat'
            onClick={onCollapse}
          >
            <X className='size-4' />
          </Button>
        )}
      </div>
    </header>
  );
}
